import gsap from 'gsap';

import { getCurrentDevice } from '../utils';

export class Menu {
	constructor( selector, options = {} ) {
		this.itemsHasSub = gsap.utils.toArray(
			`${ selector } .el-menus-list > .has-child`
		);
		this.options = options;
		this.devices = this.options.devices || [
			'Desktop',
			'Tablet',
			'Mobile',
		];
		this.currentDevice = getCurrentDevice();
		this.timer = null;
		this.enterFunc = [];
		this.leaveFunc = [];
		this.tweens = [];
		this.init();
		this.resize();
	}

	init() {
		if ( ! this.itemsHasSub?.length || ! this.options.devices.length )
			return;
		window.addEventListener( 'DOMContentLoaded', () => {
			if ( this.devices.includes( this.currentDevice ) ) {
				this.execute();
			}
		} );
		this.resize();
	}

	execute() {
		const setVars = { display: 'none' };
		const showVars = {};
		if ( this.options.animation !== 'none' ) {
			showVars.duration =
				this.options.duration !== undefined
					? this.options.duration
					: 0.3;
			showVars.ease = this.options.ease || 'power1.inOut';
		}
		if ( this.options.animation === 'expand' ) {
			setVars.height = 0;
			showVars.height = 'auto';
		} else {
			setVars.opacity = 0;
			showVars.opacity = 1;
		}
		if ( this.options.offset ) {
			const offset = this.options.offset.split( '@&' );
			if ( offset[ 1 ] === 'px' ) {
				setVars.y = offset[ 0 ];
				showVars.y = 0;
			} else {
				setVars.yPercent = offset[ 0 ];
				showVars.yPercent = 0;
			}
		}
		this.itemsHasSub.forEach( ( item, index ) => {
			const thelink = item.querySelector( 'a' );
			thelink.setAttribute( 'aria-expanded', 'false' );
			const subMenu = item.querySelector( '.el-sub-panel' );
			gsap.set( subMenu, setVars );
			this.tweens[ index ] = gsap
				.timeline( { paused: true } )
				.set( subMenu, { display: 'none' } )
				.set( subMenu, { display: 'block' } )
				.to( subMenu, showVars );
			if (
				this.options.event === 'click' ||
				this.currentDevice !== 'Desktop'
			) {
				const tempEnter = ( event ) => {
					event.preventDefault();
					this.onClick( item, index, thelink );
				};
				this.enterFunc[ index ] = tempEnter;
				thelink.addEventListener( 'click', this.enterFunc[ index ] );
			} else {
				const tempEnter = () =>
					this.showSub( item, thelink, this.tweens[ index ] );
				const tempLeave = () =>
					this.hideSub( item, thelink, this.tweens[ index ] );
				this.enterFunc[ index ] = tempEnter;
				this.leaveFunc[ index ] = tempLeave;
				item.addEventListener( 'mouseenter', this.enterFunc[ index ] );
				item.addEventListener( 'mouseleave', this.leaveFunc[ index ] );
			}
		} );
		this.itemsHasSub.forEach( ( item, index ) => {
			let hasCurrent = item.classList.contains( '.current' );
			if ( ! hasCurrent ) hasCurrent = item.querySelector( '.current' );
			if (
				hasCurrent &&
				( this.options.event === 'click' ||
					this.currentDevice !== 'Desktop' )
			) {
				const thelink = item.querySelector( 'a' );
				item.classList.add( 'active' );
				thelink.setAttribute( 'aria-expanded', 'true' );
				this.tweens[ index ].play();
			}
		} );
	}

	destroy( prevDevice ) {
		if ( this.enterFunc.length ) {
			this.itemsHasSub.forEach( ( item, index ) => {
				const thelink = item.querySelector( 'a' );
				const subMenu = item.querySelector( '.el-sub-panel' );
				if (
					this.options.event === 'click' ||
					prevDevice !== 'Desktop'
				) {
					thelink.removeEventListener(
						'click',
						this.enterFunc[ index ]
					);
					const theIcon = item.querySelector( '.icon' );
					gsap.set( theIcon, { clearProps: 'rotation' } );
				} else {
					item.removeEventListener(
						'mouseenter',
						this.enterFunc[ index ]
					);
					item.removeEventListener(
						'mouseleave',
						this.leaveFunc[ index ]
					);
				}
				thelink.setAttribute( 'aria-expanded', 'true' );
				gsap.set( subMenu, { clearProps: 'all' } );
			} );
		}
		this.enterFunc = [];
		this.leaveFunc = [];
		this.tween = [];
	}

	resize() {
		window.addEventListener( 'resize', () => {
			this.timer?.kill();
			this.timer = gsap.delayedCall( 0.5, () => {
				const prevDevice = this.currentDevice;
				this.currentDevice = getCurrentDevice();
				if ( prevDevice !== this.currentDevice ) {
					this.destroy( prevDevice );
					this.execute();
				}
			} );
		} );
	}

	onClick( itemHasSub, itemIndex, thelink ) {
		if ( itemHasSub.classList.contains( 'active' ) ) {
			const theIcon = itemHasSub.querySelector( '.icon' );
			gsap.to( theIcon, { rotate: 0, duration: 0.1 } );
			this.hideSub( itemHasSub, thelink, this.tweens[ itemIndex ] );
		} else {
			const theIcon = itemHasSub.querySelector( '.icon' );
			gsap.to( theIcon, { rotate: -180, duration: 0.1 } );
			this.itemsHasSub.forEach( ( item, index ) => {
				if ( item.classList.contains( 'active' ) ) {
					const prevLink = item.querySelector( 'a' );
					this.hideSub( item, prevLink, this.tweens[ index ] );
				}
			} );
			this.showSub( itemHasSub, thelink, this.tweens[ itemIndex ] );
		}
	}

	showSub( itemHasSub, thelink, tween ) {
		itemHasSub.classList.add( 'active' );
		thelink.setAttribute( 'aria-expanded', 'true' );
		tween.play();
	}

	hideSub( itemHasSub, thelink, tween ) {
		itemHasSub.classList.remove( 'active' );
		thelink.setAttribute( 'aria-expanded', 'false' );
		tween.reverse();
	}
}
